import React,{ forwardRef, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { Box } from '@mui/material';
import { LineChartDataItem, ChartColorConfig, BaseBlockProps } from '../types';
import {
    LINE_CHART_MARGINS,
    LINE_LEGEND_CONFIG,
    CHART_THEME,
    createAxisConfig,
} from '../../../utils/chartConfig';

interface LineChartProps {
    data: LineChartDataItem[];
    colors?: ChartColorConfig;
    stacked?: boolean;
    kedo: BaseBlockProps["kedo"];
    height?: string | number;
    xLegend?: string;
    yLegend?: string;
}

export const LineChart = forwardRef<HTMLDivElement, LineChartProps>(
    (
        {
            data,
            colors = { scheme: 'nivo' },
            stacked = true,
            kedo,
            height = '100%',
            xLegend,
            yLegend,
        },
        ref
    ) => {
        const bottomAxisConfig = useMemo(
            () => createAxisConfig(xLegend ?? kedo.t('Date'), 'bottom'),
            [kedo, xLegend]
        );

        const leftAxisConfig = useMemo(
            () => createAxisConfig(yLegend ?? kedo.t('Totals'), 'left'),
            [kedo, yLegend]
        );

        const colorConfig = useMemo(() => {
            if (colors.scheme) {
                return { scheme: colors.scheme };
            }
            if (colors.datum) {
                return { datum: colors.datum };
            }
            return { scheme: 'nivo' };
        }, [colors]);

        return (
            <Box ref={ref} sx={{ height, width: '100%' }}>
                <ResponsiveLine
                    data={data}
                    margin={LINE_CHART_MARGINS}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: 'auto',
                        max: 'auto',
                        stacked,
                        reverse: false,
                    }}
                    yFormat=" >-.2f"
                    colors={colorConfig}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        ...bottomAxisConfig,
                        legendOffset: 36,
                    }}
                    axisLeft={{
                        ...leftAxisConfig,
                        legendOffset: -40,
                    }}
                    pointSize={4}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={false}
                    legends={[LINE_LEGEND_CONFIG]}
                    theme={CHART_THEME}
                    animate
                    motionConfig="gentle"
                />
            </Box>
        );
    }
);

LineChart.displayName = 'LineChart';
